
import {first} from 'rxjs/operators';
import { Component, OnInit, Renderer2, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { FlowTemplateService } from "../../services/flow-template.service";
import { LayoutService } from "../../../layouts/services/layout.service";
import { LoaderService } from "../../../common/services/loader.service";
import { SidePanelService } from "../../../common/services/side-panel.service";

import { DynamicComponentLoaderDirective } from "../../../common/directives/dynamic-component-loader.directive";

import { Button, ButtonKind, ButtonPosition, ChangeTargetRule, FlowTemplate, Page, PageType, Question, Section, State } from "../../../common/models/flow.dto";
import { genId } from "../../../core/services/genId";
import { SidePanel } from "../../../common/models/side-panel.model";

@Component({
    selector: "app-state-builder",
    templateUrl: "./state-builder.component.html",
    styleUrls: ["./state-builder.component.scss"],
    standalone: false
})

export class StateBuilderComponent implements OnInit {
  public activePageNumber: number = 0;
  public currentPage: Page = null;
  public currentQuestion: Question = null;
  public editButtonPosition: string = "";
  public editButton: Button = null;
  public isButtonRule: boolean = false;
  public stateKey: number;
  public template: FlowTemplate = null;
  public _state: State;
    public error: any;

    public buttonSidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Rules Editor" }, { title: "Permissions" }],
        sections: [{ componentName: 'StateBuilderRules', componentData: { activeTemplate: this.template, selectedState: this.state, buttonPosition: this.editButtonPosition, activeButton: this.editButton, isButtonRule: this.isButtonRule, activePageNumber: this.activePageNumber } },
            { componentName: 'StateBuilderButtonPermissions', componentData: { activeTemplate: this.template, selectedState: this.state, activeButton: this.editButton } }],
        visible: false
    });

  public rulesSidePanel: SidePanel = new SidePanel({
    tabs: [{ title: "Rules Editor" }],
    sections: [{ componentName: 'StateBuilderRules', componentData: { activeTemplate: this.template, selectedState: this.state, buttonPosition: this.editButtonPosition, activeButton: this.editButton, isButtonRule: this.isButtonRule, activePageNumber: this.activePageNumber } }],
    visible: false
  });

  public sectionValidationSidePanel: SidePanel = new SidePanel({
    tabs: [{ title: "Section Validation" }],
    sections: [{ componentName: "StateBuilderSectionValidation", componentData: { activePage: this.currentPage, selectedState: this.state } }],
    visible: false
  });

  public stateBuilderSidePanel: SidePanel = new SidePanel({
    tabs: [{ title: "Display" }, { title: "Validation" }, { title: "Data" }],
    sections: [
      { componentName: 'StateSettings', componentData: { currentQuestion: this.currentQuestion } },
      { componentName: 'StateBuilderValidation', componentData: { currentQuestion: this.currentQuestion } },
      { componentName: 'StateBuilderData', componentData: { currentQuestion: this.currentQuestion, currentTemplate: this.template, selectedState: this.state } }
    ],
    visible: false
  });

    dialog: Dialog = new Dialog("An error has occurred during saving.  Please try again.  If the error persists, please contact support.", [new DialogButton("OK", true, () => this.onDialogConfirm())]);

  constructor(
    public route: ActivatedRoute,
    public service: FlowTemplateService,
    public renderer: Renderer2,
    public readonly router: Router,
    public loader: LoaderService,
    public layoutService: LayoutService,
    public sidePanelService: SidePanelService,
    public dialogService: DynamicDialogService
  ) { }

  ngOnInit(): void {
    let state = this.state;
    this.loader.toggleLoader(false);
    this.template = this.route.snapshot.data.template;
    if (this.state && (!this.state.sections || this.state.sections.length === 0)) {
      const section = this.makeSectionStub();
      this.state.sections.push(section);
      const page = section.pages[0];
      this.currentPage = page;
      this.activePageNumber = page.pageNumber;
    } else if (this.state) {
      this.currentPage = this.state.sections[0].pages[0];
      this.activePageNumber = this.currentPage.pageNumber;
    }

    this.sidePanelService.newSidePanel(this.rulesSidePanel);
      this.layoutService.update('StateBuilderComponent');
      this.dialogService.updateDialog(this.dialog);
  }

  get state(): State {
    if (!this.stateKey)
      this.stateKey = parseInt(this.route.snapshot.params.stateKey, 10);

    if (this.template)
      this._state = this.template.states.find(s => s.key === this.stateKey);

    return this._state;
  }

  set state(value: State) {
    this._state = value;
  }

  public addRulesEditorHandler(): void {
    this.editButtonPosition = "";
    this.editButton = null;
    this.isButtonRule = false;

    this.sidePanelService.newSidePanel(this.rulesSidePanel);
    this.sidePanelService.updateSectionData('StateBuilderRules', {
      currentPage: this.currentPage,
      activeTemplate: this.template,
      selectedState: this.state,
      buttonPosition: this.editButtonPosition,
      activeButton: this.editButton,
      isButtonRule: this.isButtonRule
    });
    this.sidePanelService.toggleSidePanel(true);
  }

  public editSectionValidationHandler(e: boolean): void {
    this.sidePanelService.newSidePanel(this.sectionValidationSidePanel);
    this.sidePanelService.updateSectionData("StateBuilderSectionValidation",
      {
        activePage: this.currentPage,
        selectedState: this.state
      });
    this.sidePanelService.toggleSidePanel(e);
  }

  public goToDiagram(e: Event): void {
    this.saveDraft();
  }

  public groomPages(): void {
    this.template.states.forEach(s => {
      if (!s.sections.length) return;
      let pageNumber = 1;
      for (let t = 0; t < s.sections.length; t++) {
        const section = s.sections[t];
        if (!section.pages.length)
          return;

        // Renumber in case drag-sorted
        for (let p = 0; p < section.pages.length; p++) {
          section.pages[p].section = section.id;
          section.pages[p].pageNumber = pageNumber;
          pageNumber += 1;
        }

        section.pages.sort((a, b) => a.pageNumber - b.pageNumber);

        section.pages.forEach(p => {
          p.questions = this.removeEmptyQuestions(p);
        });
      }
    });
  }

  public makeSectionStub(): Section {
    const page = this.makePageStub();
    return {
      id: genId(),
      name: "Section 1",
      pages: [page]
    } as Section;
  }

  public makePageStub(): Page {
    return {
      pageNumber: 1,
      pageType: PageType.Input,
      subtitle: "Page 1",
      questions: [],
      section: "Section 1",
      incomingRules: new Array<ChangeTargetRule>(),
      isSectionHead: true,
      buttons: [
        {
          id: genId(),
          position: ButtonPosition.Right,
          label: "Next",
          type: ButtonKind.Next,
          visibleRule: null,
          enabledRule: null,
          targetPageRule: null,
          targetTransitionRule: null,
          disableTypeChange: false
        } as Button
      ]
    } as Page;
  }

  public openSettings(componentRef: DynamicComponentLoaderDirective): void {
    //update value of the currentQuestion for each section with the latest data
    for (let section of this.stateBuilderSidePanel.sections) {
      section.componentData.currentQuestion = this.currentQuestion;
    }
    this.stateBuilderSidePanel.injector = componentRef.injector;
    this.stateBuilderSidePanel.sections[2].componentData.currentTemplate = this.template;
    this.stateBuilderSidePanel.sections[2].componentData.selectedState = this.state;
    this.sidePanelService.newSidePanel(this.stateBuilderSidePanel);
    this.sidePanelService.toggleSidePanel(true);
  }

  public removeEmptyQuestions(p: Page): Question[][] {
    const questionGroups = [];

    p.questions.forEach(questionGroup => {
      let realQuestions = [];
      questionGroup.forEach(question => {
        if (question.type) realQuestions.push(question);
      });

      if (realQuestions.length) questionGroups.push(realQuestions);
    });

    return questionGroups;
  }

  public saveDraft(): void {
    this.loader.toggleLoader(true);
    this.groomPages();

    const save = this.service.saveAsDraft(this.template);
    save.pipe(first()).subscribe((result) => {
      this.template.majorVersion = result.majorVersion;
      this.template.minorVersion = result.minorVersion;
      this.loader.toggleLoader(false);
      this.router.navigate([`/builder/flow/${this.template.workflowId}`]);
    }, (error) => {
        console.error(error);
            this.error = error;
            this.loader.toggleLoader(false);
            this.dialog.toggleDialog(true);
            this.dialogService.updateDialog(this.dialog);
    });

  }

  public setCurrentPage(page: Page): void {
    for (let s = 0; s < this.state.sections.length; s++) {
      const sect = this.state.sections[s];
      this.currentPage = sect.pages.find(p => p.pageNumber === page.pageNumber);
      if (this.currentPage) break;
    }
  }

  //from state-builder-page
  public setSelectedQuestionHandler(q: Question): void {
    if (!q.config)
      q.config = {
        domain: undefined,
        displayProperty: undefined,
        idProperty: undefined
      };
    this.currentQuestion = q;

    for (let section of this.stateBuilderSidePanel.sections) {
      section.componentData.currentQuestion = this.currentQuestion;
      this.sidePanelService.updateSectionData(section.componentName, { currentQuestion: this.currentQuestion, currentTemplate: this.template, selectedState: this.state });
    }

  }

    public showButtonRulesEditorHandler(button: Button): void {
        this.editButtonPosition = button.position;
        this.editButton = button;
        this.isButtonRule = true;

        this.sidePanelService.newSidePanel(this.buttonSidePanel);
        this.sidePanelService.updateSectionData('StateBuilderRules', {
            currentPage: this.currentPage,
            activeTemplate: this.template,
            selectedState: this.state,
            buttonPosition: this.editButtonPosition,
            activeButton: this.editButton,
            isButtonRule: this.isButtonRule
        });
        this.sidePanelService.updateSectionData('StateBuilderButtonPermissions', {
            currentPage: this.currentPage,
            activeTemplate: this.template,
            selectedState: this.state,
            activeButton: this.editButton
        });

        this.sidePanelService.toggleSidePanel(true);
    }

    public onDialogConfirm(): void {
    console.error(this.error);
  }

  public onDialogClose(): void {
  }

}
